import { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { ArrowLeft, Loader2, MapPin } from 'lucide-react';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';

export default function MapViewPage() {
  const { id: campaignId } = useParams();
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<maplibregl.Map | null>(null);
  const [mapError, setMapError] = useState<string | null>(null);

  const { data: campaign, isLoading: campaignLoading } = useQuery({
    queryKey: ['campaign', campaignId],
    queryFn: () => campaignId ? api.getCampaign(campaignId) : Promise.resolve(null),
    enabled: !!campaignId,
  });

  const { data: locations, isLoading: locationsLoading } = useQuery({
    queryKey: ['target-locations', campaignId],
    queryFn: () => campaignId ? api.getTargetLocations(campaignId) : Promise.resolve([]),
    enabled: !!campaignId,
  });

  useEffect(() => {
    if (!mapContainer.current || map.current) return;

    try {
      map.current = new maplibregl.Map({
        container: mapContainer.current,
        style: 'https://basemaps.cartocdn.com/gl/positron-gl-style/style.json',
        center: [0, 20],
        zoom: 2,
      });

      map.current.addControl(new maplibregl.NavigationControl(), 'top-right');

      map.current.on('error', () => {
        setMapError('Failed to load map. Please check your connection.');
      });
    } catch {
      setMapError('Failed to initialize map.');
    }

    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!map.current || !locations) return;

    locations.forEach((location) => {
      if (location.lat && location.lng) {
        const el = document.createElement('div');
        el.className = 'custom-marker';
        el.innerHTML = `<div class="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-primary-foreground font-bold shadow-lg border-2 border-white">${location.status === 'completed' ? '✓' : location.status === 'in_progress' ? '◐' : '○'}</div>`;

        new maplibregl.Marker(el)
          .setLngLat([location.lng, location.lat])
          .setPopup(
            new maplibregl.Popup({ offset: 25 }).setHTML(`
              <div class="p-2">
                <h3 class="font-semibold">${location.name}</h3>
                <p class="text-sm text-gray-600">${location.address || ''}</p>
                <p class="text-xs mt-1">Status: ${location.status}</p>
              </div>
            `)
          )
          .addTo(map.current!);
      }
    });

    if (locations.length > 0) {
      const bounds = new maplibregl.LngLatBounds();
      locations.forEach((loc) => {
        if (loc.lat && loc.lng) {
          bounds.extend([loc.lng, loc.lat]);
        }
      });
      map.current.fitBounds(bounds, { padding: 50 });
    }
  }, [locations]);

  if (campaignLoading || locationsLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link to={`/campaigns/${campaignId}`}>
            <Button variant="ghost" size="icon">
              <ArrowLeft className="w-4 h-4" />
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold">{campaign?.name || 'Campaign'} Map</h1>
            <p className="text-muted-foreground">
              {locations?.length || 0} locations
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2 text-sm">
          <span className="flex items-center">
            <span className="w-3 h-3 rounded-full bg-gray-400 mr-1"></span> Pending
          </span>
          <span className="flex items-center">
            <span className="w-3 h-3 rounded-full bg-blue-500 mr-1"></span> In Progress
          </span>
          <span className="flex items-center">
            <span className="w-3 h-3 rounded-full bg-green-500 mr-1"></span> Completed
          </span>
        </div>
      </div>

      {mapError ? (
        <Card className="h-[600px] flex items-center justify-center">
          <div className="text-center text-muted-foreground">
            <MapPin className="w-12 h-12 mx-auto mb-4" />
            <p className="font-medium">{mapError}</p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => {
                setMapError(null);
                if (map.current) {
                  map.current.remove();
                  map.current = null;
                }
              }}
            >
              Retry
            </Button>
          </div>
        </Card>
      ) : (
        <div ref={mapContainer} className="h-[600px] rounded-lg border border-border overflow-hidden" />
      )}

      {locations && locations.length === 0 && !mapError && (
        <Card className="p-6 text-center text-muted-foreground">
          <MapPin className="w-12 h-12 mx-auto mb-4" />
          <p>No target locations added to this campaign yet.</p>
          <p className="text-sm mt-2">Add locations to see them on the map.</p>
        </Card>
      )}
    </div>
  );
}
